import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/Breadcrumbs";
import { GuiasRelacionadas } from "@/components/GuiasRelacionadas";
import hero1280Asset from "../assets/hero-1280.webp.asset.json";
import hero1920Asset from "../assets/hero-1920.webp.asset.json";
import hero640Asset from "../assets/hero-640.webp.asset.json";

const srcSet = `${hero640Asset.url} 640w, ${hero1280Asset.url} 1280w, ${hero1920Asset.url} 1920w`;
const OG_IMAGE = `https://elpajardebenito.es${hero1280Asset.url}`;
const URL_CANON = "https://elpajardebenito.es/casa-rural-guadalajara-grupos";

const faqs = [
  { q: "¿Cuántas personas caben en El Pajar de Benito?", a: "El alojamiento completo acoge hasta 14 personas distribuidas en 6 habitaciones dobles con baño, ideal para grupos de amigos, familias y celebraciones." },
  { q: "¿Se puede alquilar la casa completa para un grupo?", a: "Sí. Ofrecemos alquiler íntegro de la casa rural para grupos, con exclusividad total del jardín, cocina, salón con chimenea y zonas comunes." },
  { q: "¿A qué distancia está de Madrid?", a: "El Ordial (Guadalajara) está a 1 h 30 min de Madrid por la A-2 y la CM-1101, en plena Sierra Norte. Es una escapada rural perfectamente accesible en coche." },
  { q: "¿Admite mascotas y niños?", a: "Sí. Somos pet friendly y disponemos de espacio exterior amplio, cuna y trona bajo petición para familias con niños pequeños." },
  { q: "¿Hay tarifas especiales para grupos?", a: "Consulta disponibilidad y precios para reserva completa: aplicamos tarifas específicas para grupos de 10 a 14 personas y estancias de 2 o más noches." },
];

export const Route = createFileRoute("/casa-rural-guadalajara-grupos")({
  head: () => ({
    meta: [
      { title: "Casa rural para grupos en Guadalajara (hasta 14) · El Pajar de Benito" },
      { name: "description", content: "Casa rural completa en Guadalajara para grupos de hasta 14 personas. 6 habitaciones con baño, chimenea, jardín y a 1 h 30 de Madrid. Reserva directa." },
      { property: "og:title", content: "Casa rural para grupos en Guadalajara · hasta 14 personas" },
      { property: "og:description", content: "Alquiler íntegro para grupos en la Sierra Norte de Guadalajara: 6 dobles con baño, chimenea y jardín privado." },
      { property: "og:url", content: URL_CANON },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL_CANON }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbJsonLd([
            { label: "Inicio", to: "/" },
            { label: "Casa rural para grupos en Guadalajara", to: "/casa-rural-guadalajara-grupos" },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Casa rural para grupos" }]} />
      <section className="relative h-[55vh] min-h-[380px] w-full overflow-hidden">
        <img src={hero1280Asset.url} srcSet={srcSet} sizes="100vw" alt="Casa rural para grupos en Guadalajara" width={1920} height={1280} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 mx-auto flex h-full max-w-5xl flex-col justify-end px-4 pb-16 md:px-6">
          <p className="text-xs uppercase tracking-[0.28em] text-white/80">Alquiler íntegro · Sierra Norte</p>
          <h1 className="mt-3 font-serif text-4xl text-white md:text-5xl">Casa rural para grupos en Guadalajara</h1>
          <p className="mt-4 max-w-2xl text-white/90">Hasta 14 personas en 6 habitaciones dobles con baño privado. Chimenea, jardín y a 1 h 30 min de Madrid.</p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 md:px-6">
        <h2 className="font-serif text-3xl text-foreground">Una casa rural pensada para grupos</h2>
        <p className="mt-4 text-muted-foreground">El Pajar de Benito es una <strong>casa rural completa en Guadalajara</strong> ideal para <strong>grupos de amigos, familias y celebraciones</strong>. Reúne a los tuyos en El Ordial, en plena Sierra del Alto Rey, con exclusividad total del alojamiento.</p>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {[
            { t: "14 plazas", d: "6 habitaciones dobles, todas con baño privado." },
            { t: "Zonas comunes", d: "Salón con chimenea, cocina equipada y comedor grande." },
            { t: "Jardín privado", d: "Espacio exterior, porche y barbacoa para todo el grupo." },
            { t: "Cerca de Madrid", d: "1 h 30 min por A-2 · escapada de fin de semana." },
            { t: "Pet friendly", d: "Admitimos mascotas bajo petición." },
            { t: "Reserva directa", d: "Sin intermediarios ni comisiones ocultas." },
          ].map((b) => (
            <div key={b.t} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-lg">{b.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-muted/40">
        <div className="mx-auto max-w-4xl px-4 py-16 md:px-6">
          <h2 className="font-serif text-3xl text-foreground">Perfecto para</h2>
          <ul className="mt-6 space-y-3 text-muted-foreground">
            <li>• <strong>Cumpleaños y celebraciones familiares</strong> con exclusividad del jardín y la casa.</li>
            <li>• <strong>Escapadas de amigos</strong> a la Sierra Norte con senderismo y cielos estrellados.</li>
            <li>• <strong>Retiros y team building</strong> de empresas pequeñas (hasta 14 personas).</li>
            <li>• <strong>Reencuentros y despedidas</strong> en un entorno rural tranquilo.</li>
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 md:px-6">
        <h2 className="font-serif text-3xl text-foreground">Preguntas frecuentes</h2>
        <div className="mt-8 space-y-6">
          {faqs.map((f) => (
            <div key={f.q}>
              <h3 className="font-medium text-foreground">{f.q}</h3>
              <p className="mt-2 text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <GuiasRelacionadas slugs={["casa-rural-grupos-cerca-madrid", "que-ver-sierra-norte-guadalajara", "subida-alto-rey"]} />

      <section className="mx-auto max-w-3xl px-4 pb-24 text-center md:px-6">
        <h2 className="font-serif text-3xl text-foreground md:text-4xl">Consulta disponibilidad para tu grupo</h2>
        <p className="mt-4 text-muted-foreground">Reserva directa sin comisiones. Te respondemos en menos de 24 h.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/reserva" className="rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">Ver disponibilidad</Link>
          <Link to="/contacto" className="rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-muted">Contactar</Link>
        </div>
      </section>
    </>
  );
}